/* src/components/ContactForm.js */

import React, { useEffect, useState } from "react";
import { FaSpinner, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa";
import DOMPurify from "dompurify";
import "./ContactForm.css";

function ContactForm({ leadSource, initialMessage = "" }) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: initialMessage,
    website: "", // Honeypot field to catch bots
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    if (!initialMessage) return;
    setFormData((current) => ({
      ...current,
      message: current.message || initialMessage,
    }));
  }, [initialMessage]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Basic validation before submission
  const validateForm = () => {
    // Name validation
    if (formData.name.trim().length < 2) {
      setErrorMessage("Please enter a valid name (at least 2 characters)");
      return false;
    }
    
    // Email validation with regex
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setErrorMessage("Please enter a valid email address");
      return false;
    }
    
    // Message validation
    if (formData.message.trim().length < 10) {
      setErrorMessage("Please enter a message with at least 10 characters");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setShowError(false);
    
    // Validate form before submission
    if (!validateForm()) {
      setShowError(true);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Sanitize inputs before submission
      const sanitizedData = {
        name: DOMPurify.sanitize(formData.name),
        email: DOMPurify.sanitize(formData.email),
        message: DOMPurify.sanitize(formData.message),
        website: formData.website, // Keep honeypot field as is
        leadSource: leadSource || undefined, // Include lead source if provided
      };
      
      // Send data to backend API (including honeypot field to detect bots)
      // Use the standard email endpoint with OAuth
      const apiUrl = 'contact/submit';
      console.log('Submitting form to:', apiUrl);
      console.log('Form data being submitted:', sanitizedData);
      console.log('Email being submitted:', sanitizedData.email);
      
      // Import the api utility
      const api = await import('../utils/api').then(module => module.default);
      
      const response = await api.post(apiUrl, sanitizedData);
      
      // Axios returns response data directly
      console.log('Form submitted successfully', response.data);
      console.log('Email sent status:', response.data.emailSent);
      setSubmitted(true);
      setEmailSent(response.data.emailSent);
      // Reset form
      setFormData({ name: "", email: "", message: initialMessage, website: "" });
    } catch (error) {
      // Handle axios error
      let errorMsg = 'There was an error submitting your message. Please try again.';
      
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        const errorData = error.response.data;
        
        if (errorData && errorData.errors && errorData.errors.length > 0) {
          // Use the first validation error message
          errorMsg = errorData.errors[0].msg;
        } else if (errorData && errorData.message) {
          // Use the general error message
          errorMsg = errorData.message;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMsg = 'Network error. Please check your connection and try again.';
      } else {
        // Something happened in setting up the request that triggered an Error
        errorMsg = 'An unexpected error occurred. Please try again.';
      }
      
      setErrorMessage(errorMsg);
      setShowError(true);
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-form-container">
      {submitted ? (
        <div className="success-message fade-in">
          <FaCheckCircle className="success-icon" aria-hidden="true" />
          <h3>Thank you for contacting Tharpe Industries!</h3>
          {emailSent ? (
            <p>A confirmation has been sent to your email. You'll receive a response within 24 hours to discuss your IT needs.</p>
          ) : (
            <p>Your message has been received. You'll receive a response within 24 hours to discuss your IT needs.</p>
          )}
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="contact-form">
          {/* Honeypot field - hidden from real users, bots will fill it out */}
          <div className="website-field" aria-hidden="true" style={{ position: 'absolute', left: '-9999px' }}>
            <label htmlFor="website">Website (Leave this empty)</label>
            <input
              type="text"
              id="website"
              name="website"
              value={formData.website}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>
          
          {/* Show error message if there's a validation error */}
          {showError && (
            <div className="error-message">
              <FaExclamationTriangle className="error-icon" aria-hidden="true" />
              <span>{errorMessage}</span>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Your name"
              required
              disabled={isSubmitting}
              minLength="2"
              maxLength="100"
            />
          </div>
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Your email address"
              required
              disabled={isSubmitting}
            />
          </div>
          <div className="form-group">
            <label htmlFor="message">Message</label>
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell me about your IT needs"
              required
              disabled={isSubmitting}
              minLength="10"
              maxLength="1000"
              rows="5"
            ></textarea>
          </div>
          <div className="form-note">
            <small>* All fields are required. Your information won't be shared with third parties.</small>
          </div>
          <button 
            type="submit" 
            className="submit-button btn btn-primary"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <FaSpinner className="spinner-icon" aria-hidden="true" />
                Sending...
              </>
            ) : (
              'Submit'
            )}
          </button>
        </form>
      )}
    </div>
  );
}

export default ContactForm;
